import { WorkoutInputs as WorkoutInputsType, Bottle } from '../types';

interface Props {
  inputs: WorkoutInputsType;
  onChange: (inputs: WorkoutInputsType) => void;
}

export default function WorkoutInputs({ inputs, onChange }: Props) {
  const addBottle = () => {
    const newBottle: Bottle = {
      id: Date.now().toString(),
      size: '500ml',
      label: generateBottleLabel(inputs.bottles.length, '500ml')
    };
    onChange({ ...inputs, bottles: [...inputs.bottles, newBottle] });
  };

  const removeBottle = (id: string) => {
    const updatedBottles = inputs.bottles.filter(b => b.id !== id);
    const relabeled = updatedBottles.map((bottle, index) => ({
      ...bottle,
      label: generateBottleLabel(index, bottle.size)
    }));
    onChange({ ...inputs, bottles: relabeled });
  };

  const updateBottleSize = (id: string, size: '250ml' | '500ml' | '750ml' | '1.5L' | '3L') => {
    const updatedBottles = inputs.bottles.map((b, index) => 
      b.id === id ? { ...b, size, label: generateBottleLabel(index, size) } : b
    );
    onChange({ ...inputs, bottles: updatedBottles });
  };

  const generateBottleLabel = (index: number, size: string): string => {
    if (size === '250ml') {
      const flaskCount = inputs.bottles.slice(0, index + 1).filter(b => b.size === '250ml').length;
      return `Flask ${flaskCount}`;
    }
    if (size === '1.5L' || size === '3L') {
      return 'Bladder';
    }
    const bottleCount = inputs.bottles.slice(0, index + 1).filter(b => b.size === '500ml' || b.size === '750ml').length;
    return `Bottle ${bottleCount}`;
  };

  return (
    <div className="space-y-8">
      {/* Sport Selection */}
      <div>
        <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
          Sport / Discipline
        </label>
        <div className="grid grid-cols-3 gap-3">
          {(['running', 'cycling', 'triathlon'] as const).map((sport) => (
            <button
              key={sport}
              onClick={() => onChange({ ...inputs, sport })}
              className={`py-3 px-4 rounded-lg font-bold uppercase tracking-wide transition-all ${
                inputs.sport === sport
                  ? 'bg-[#00FF88] text-black'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              {sport}
            </button>
          ))}
        </div>

        {/* Race Day Toggle */}
        <div className="mt-4 flex items-center justify-between bg-white/5 rounded-lg p-4">
          <div>
            <div className="text-white font-bold">Race Day</div>
            <div className="text-white/60 text-sm">Enable race-specific fueling protocol</div>
          </div>
          <button
            onClick={() => onChange({ ...inputs, isRaceDay: !inputs.isRaceDay })}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              inputs.isRaceDay ? 'bg-[#00FF88]' : 'bg-white/20'
            }`}
          >
            <div
              className={`absolute top-1 left-1 w-5 h-5 bg-black rounded-full transition-transform ${
                inputs.isRaceDay ? 'translate-x-7' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Duration Inputs */}
      {inputs.sport === 'triathlon' ? (
        <div className="space-y-6">
          <label className="block text-sm font-bold text-white uppercase tracking-wider">
            Triathlon Segments
          </label>

          {/* Swim */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm font-medium">Swim</span>
              <span className="text-[#00FF88] font-bold">{inputs.swimMinutes}min</span>
            </div>
            <input
              type="range"
              min="15"
              max="120"
              step="5"
              value={inputs.swimMinutes}
              onChange={(e) => onChange({ ...inputs, swimMinutes: parseInt(e.target.value) })}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
            />
          </div>

          {/* Bike */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm font-medium">Bike</span>
              <span className="text-[#00FF88] font-bold">
                {inputs.bikeHours}h {inputs.bikeMinutes}min
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/60 text-xs mb-1 block">Hours</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={inputs.bikeHours}
                  onChange={(e) => onChange({ ...inputs, bikeHours: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs mb-1 block">Minutes</label>
                <input
                  type="range"
                  min="0"
                  max="55"
                  step="5"
                  value={inputs.bikeMinutes}
                  onChange={(e) => onChange({ ...inputs, bikeMinutes: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
                />
              </div>
            </div>
          </div>

          {/* Run */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-sm font-medium">Run</span>
              <span className="text-[#00FF88] font-bold">
                {inputs.runHours}h {inputs.runMinutes}min
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/60 text-xs mb-1 block">Hours</label>
                <input
                  type="range"
                  min="0"
                  max="8"
                  value={inputs.runHours}
                  onChange={(e) => onChange({ ...inputs, runHours: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs mb-1 block">Minutes</label>
                <input
                  type="range"
                  min="0"
                  max="55"
                  step="5"
                  value={inputs.runMinutes}
                  onChange={(e) => onChange({ ...inputs, runMinutes: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
            Duration
          </label>
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/80">Workout Length</span>
            <span className="text-[#00FF88] font-bold">
              {inputs.durationHours}h {inputs.durationMinutes}min
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/60 text-xs mb-2 block">Hours</label>
              <input
                type="range"
                min="0"
                max="12"
                value={inputs.durationHours}
                onChange={(e) => onChange({ ...inputs, durationHours: parseInt(e.target.value) })}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
              />
            </div>
            <div>
              <label className="text-white/60 text-xs mb-2 block">Minutes</label>
              <input
                type="range"
                min="0"
                max="55"
                step="5"
                value={inputs.durationMinutes}
                onChange={(e) => onChange({ ...inputs, durationMinutes: parseInt(e.target.value) })}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#00FF88]"
              />
            </div>
          </div>
        </div>
      )}

      {/* Intensity */}
      <div>
        <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
          Intensity
        </label>
        <div className="grid grid-cols-3 gap-3">
          {(['easy', 'moderate', 'hard'] as const).map((level) => (
            <button
              key={level}
              onClick={() => onChange({ ...inputs, intensity: level })}
              className={`py-3 px-4 rounded-lg font-bold uppercase tracking-wide transition-all ${
                inputs.intensity === level
                  ? 'bg-[#00FF88] text-black'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Carb Tolerance - 4 TIERS */}
      <div>
        <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
          Carb Tolerance
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onChange({ ...inputs, carbTolerance: 'building' })}
            className={`py-4 px-3 rounded-lg font-bold transition-all ${
              inputs.carbTolerance === 'building'
                ? 'bg-[#00FF88] text-black'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <div className="text-sm uppercase tracking-wide">Building</div>
            <div className="text-xs mt-1 opacity-70 font-normal">Start Here</div>
          </button>
          <button
            onClick={() => onChange({ ...inputs, carbTolerance: 'intermediate' })}
            className={`py-4 px-3 rounded-lg font-bold transition-all ${
              inputs.carbTolerance === 'intermediate'
                ? 'bg-[#00FF88] text-black'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <div className="text-sm uppercase tracking-wide">Intermediate</div>
            <div className="text-xs mt-1 opacity-70 font-normal">Getting Dialed</div>
          </button>
          <button
            onClick={() => onChange({ ...inputs, carbTolerance: 'performance' })}
            className={`py-4 px-3 rounded-lg font-bold transition-all ${
              inputs.carbTolerance === 'performance'
                ? 'bg-[#00FF88] text-black'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <div className="text-sm uppercase tracking-wide">Performance</div>
            <div className="text-xs mt-1 opacity-70 font-normal">Gut Trained</div>
          </button>
          <button
            onClick={() => onChange({ ...inputs, carbTolerance: 'elite' })}
            className={`py-4 px-3 rounded-lg font-bold transition-all ${
              inputs.carbTolerance === 'elite'
                ? 'bg-[#00FF88] text-black'
                : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <div className="text-sm uppercase tracking-wide">Elite</div>
            <div className="text-xs mt-1 opacity-70 font-normal">Race Fueling</div>
          </button>
        </div>
        <p className="text-white/50 text-xs mt-3 leading-relaxed">
          {inputs.carbTolerance === 'building' && 'New to fueling during workouts or working up to higher carb intake. GI-sensitive. Start conservative.'}
          {inputs.carbTolerance === 'intermediate' && 'Comfortable fueling during workouts, some experience with carbs on the move.'}
          {inputs.carbTolerance === 'performance' && 'Regularly fuel at high volumes, gut-trained over months of training.'}
          {inputs.carbTolerance === 'elite' && 'Only select if you already fuel at 90g/hr consistently with zero GI issues.'}
        </p>
        {inputs.carbTolerance === 'elite' && (
          <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
            <i className="ri-alert-line text-amber-500 text-sm mt-0.5"></i>
            <p className="text-amber-500/90 text-xs">
              Only select if you already fuel at 90g/hr consistently with zero GI issues.
            </p>
          </div>
        )}
      </div>

      {/* Conditions */}
      <div>
        <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
          Conditions / Heat
        </label>
        <div className="grid grid-cols-3 gap-3">
          {(['cool', 'moderate', 'hot'] as const).map((temp) => (
            <button
              key={temp}
              onClick={() => onChange({ ...inputs, heat: temp })}
              className={`py-3 px-4 rounded-lg font-bold uppercase tracking-wide transition-all ${
                inputs.heat === temp
                  ? 'bg-[#00FF88] text-black'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              {temp}
            </button>
          ))}
        </div>

        {/* High Sweat Rate Toggle */}
        <div className="mt-4 flex items-center justify-between bg-white/5 rounded-lg p-4">
          <div>
            <div className="text-white font-bold text-sm">High Sweat Rate</div>
            <div className="text-white/60 text-xs">You sweat significantly more than average</div>
          </div>
          <button
            onClick={() => onChange({ ...inputs, highSweatRate: !inputs.highSweatRate })}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              inputs.highSweatRate ? 'bg-[#00FF88]' : 'bg-white/20'
            }`}
          >
            <div
              className={`absolute top-1 left-1 w-5 h-5 bg-black rounded-full transition-transform ${
                inputs.highSweatRate ? 'translate-x-7' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        {inputs.highSweatRate && (
          <div className="mt-3 bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-lg p-3 flex items-start gap-2">
            <i className="ri-information-line text-[#00FF88] text-sm mt-0.5"></i>
            <p className="text-white/80 text-xs">
              Consider a dedicated water bottle alongside your carb bottle.
            </p>
          </div>
        )}
      </div>

      {/* Caffeine */}
      <div>
        <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
          Caffeine Preference
        </label>
        <div className="grid grid-cols-3 gap-3">
          {(['none', 'moderate', 'high'] as const).map((level) => (
            <button
              key={level}
              onClick={() => onChange({ ...inputs, caffeinePreference: level })}
              className={`py-3 px-4 rounded-lg font-bold uppercase tracking-wide transition-all ${
                inputs.caffeinePreference === level
                  ? 'bg-[#00FF88] text-black'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Bottle Builder */}
      <div>
        <label className="block text-sm font-bold text-white mb-3 uppercase tracking-wider">
          Your Bottles
        </label>
        <p className="text-white/60 text-sm mb-4">What are you carrying?</p>
        
        <div className="space-y-3 mb-4">
          {inputs.bottles.map((bottle) => (
            <div key={bottle.id} className="bg-white/5 rounded-lg p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="text-white font-bold text-sm mb-2">{bottle.label}</div>
                <select
                  value={bottle.size}
                  onChange={(e) => updateBottleSize(bottle.id, e.target.value as any)}
                  className="w-full bg-black/50 text-white border border-white/20 rounded px-3 py-2 text-sm cursor-pointer"
                >
                  <option value="250ml">250ml Flask</option>
                  <option value="500ml">500ml Bottle</option>
                  <option value="750ml">750ml Bottle</option>
                  <option value="1.5L">1.5L CamelBak Bladder</option>
                  <option value="3L">3L CamelBak Bladder</option>
                </select>
              </div>
              <button
                onClick={() => removeBottle(bottle.id)}
                className="text-white/40 hover:text-red-500 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          ))}
        </div>

        {inputs.bottles.length < 4 && (
          <button
            onClick={addBottle}
            className="w-full py-3 bg-white/5 hover:bg-white/10 text-white font-bold rounded-lg transition-all border border-white/10 hover:border-[#00FF88]/50"
          >
            <i className="ri-add-line mr-2"></i>
            Add Bottle
          </button>
        )}
      </div>
    </div>
  );
}